import { useEffect, useRef, useState } from 'react'
import { supabase } from '../../lib/supabase.js'
import { apiFetch } from '../../lib/api.js'
import Modal from '../ui/Modal.jsx'

const BUCKET = 'music-tracks'
const MAX_SIZE_MB = 10

async function uploadAudio(file) {
  const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.mp3`
  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(path, file, { upsert: false, contentType: 'audio/mpeg' })
  if (error) throw new Error(error.message)
  const { data: { publicUrl } } = supabase.storage.from(BUCKET).getPublicUrl(path)
  return publicUrl
}

function MusicCard({ musique, isPlaying, onPlay, onEdit, onDelete }) {
  return (
    <div className={`border rounded-2xl p-4 flex items-center gap-4 group transition ${
      isPlaying
        ? 'border-blue-500/50 bg-blue-500/10'
        : 'border-white/10 bg-white/5 hover:bg-white/8'
    }`}>
      <button
        onClick={onPlay}
        title={isPlaying ? 'Arrêter' : 'Écouter'}
        className={`w-12 h-12 rounded-full flex items-center justify-center text-xl shrink-0 transition ${
          isPlaying
            ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30'
            : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
        }`}
      >
        {isPlaying ? '⏹' : '▶'}
      </button>

      <div className="flex-1 min-w-0">
        <div className={`font-medium truncate flex items-center gap-1.5 ${isPlaying ? 'text-blue-300' : 'text-white'}`}>
          {isPlaying && <span className="animate-pulse">🎵</span>}
          {musique.titre}
        </div>
        <div className="text-white/30 text-xs mt-0.5">MP3</div>
      </div>

      <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={onEdit}
          className="bg-white/10 text-white text-xs px-3 py-1.5 rounded-lg hover:bg-white/20 transition"
        >
          ✏️ Renommer
        </button>
        <button
          onClick={onDelete}
          className="bg-red-500/20 text-red-400 text-xs px-3 py-1.5 rounded-lg hover:bg-red-500 hover:text-white transition"
        >
          🗑
        </button>
      </div>
    </div>
  )
}

export default function AdminMusicPage() {
  const [musiques, setMusiques] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [playingId, setPlayingId] = useState(null)
  const [modal, setModal] = useState(null) // null | 'add' | musique-objet (renommer)
  const [saving, setSaving] = useState(false)

  const [titre, setTitre] = useState('')
  const [file, setFile] = useState(null)
  const fileRef = useRef()
  const audioRef = useRef(null)

  async function fetchMusiques() {
    setLoading(true)
    try {
      const data = await apiFetch('/api/musiques')
      setMusiques(data)
    } catch (e) {
      setError(e.message)
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchMusiques()
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
    }
  }, [])

  function handlePlay(musique) {
    if (playingId === musique.id) {
      audioRef.current?.pause()
      audioRef.current = null
      setPlayingId(null)
      return
    }
    audioRef.current?.pause()
    audioRef.current = null

    const audio = new Audio(musique.url)
    audio.addEventListener('ended', () => setPlayingId(null))
    audio.play().catch(() => setError('Impossible de lire ce fichier audio.'))
    audioRef.current = audio
    setPlayingId(musique.id)
  }

  async function handleAdd(e) {
    e.preventDefault()
    if (!file) return setError('Sélectionne un fichier MP3.')
    setSaving(true)
    setError('')
    try {
      const url = await uploadAudio(file)
      await apiFetch('/api/musiques', {
        method: 'POST',
        body: JSON.stringify({ titre: titre.trim(), url }),
      })
      closeModal()
      await fetchMusiques()
    } catch (e) {
      setError(e.message)
    }
    setSaving(false)
  }

  async function handleRename(e) {
    e.preventDefault()
    setSaving(true)
    setError('')
    try {
      await apiFetch(`/api/musiques/${modal.id}`, {
        method: 'PUT',
        body: JSON.stringify({ titre: titre.trim() }),
      })
      closeModal()
      await fetchMusiques()
    } catch (e) {
      setError(e.message)
    }
    setSaving(false)
  }

  async function handleDelete(musique) {
    if (!confirm(`Supprimer "${musique.titre}" ?`)) return
    setError('')
    if (playingId === musique.id) {
      audioRef.current?.pause()
      audioRef.current = null
      setPlayingId(null)
    }
    try {
      await apiFetch(`/api/musiques/${musique.id}`, { method: 'DELETE' })
      setMusiques(ms => ms.filter(m => m.id !== musique.id))
    } catch (e) {
      setError(e.message)
    }
  }

  function openAdd() {
    setTitre('')
    setFile(null)
    setError('')
    setModal('add')
  }

  function openRename(musique) {
    setTitre(musique.titre)
    setError('')
    setModal(musique)
  }

  function closeModal() {
    setModal(null)
    setTitre('')
    setFile(null)
    setError('')
    if (fileRef.current) fileRef.current.value = ''
  }

  function handleFileChange(e) {
    const f = e.target.files[0]
    if (!f) return
    if (f.size > MAX_SIZE_MB * 1024 * 1024) {
      setError(`Fichier trop volumineux (max ${MAX_SIZE_MB} Mo).`)
      return
    }
    setError('')
    setFile(f)
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-white">Musiques de fond</h1>
        <button onClick={openAdd} className="btn-primary px-4 py-2">
          + Ajouter une musique
        </button>
      </div>

      {error && !modal && (
        <div className="bg-red-500/15 border border-red-500/30 text-red-300 rounded-lg px-4 py-2 mb-4">{error}</div>
      )}

      {loading ? (
        <div className="text-white/40 py-12 text-center">Chargement…</div>
      ) : musiques.length === 0 ? (
        <div className="text-center py-16">
          <div className="text-5xl mb-3">🎵</div>
          <p className="text-white/40">Aucune musique. Commence par en ajouter une !</p>
          <button onClick={openAdd} className="mt-4 btn-primary px-4 py-2">
            + Ajouter une musique
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-3 max-w-2xl">
          {musiques.map(musique => (
            <MusicCard
              key={musique.id}
              musique={musique}
              isPlaying={playingId === musique.id}
              onPlay={() => handlePlay(musique)}
              onEdit={() => openRename(musique)}
              onDelete={() => handleDelete(musique)}
            />
          ))}
        </div>
      )}

      {/* Modal — Ajouter une musique */}
      <Modal
        open={modal === 'add'}
        onClose={closeModal}
        title="Ajouter une musique"
      >
        <form onSubmit={handleAdd} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white/80 mb-1">Titre du morceau</label>
            <input
              required
              value={titre}
              onChange={e => setTitre(e.target.value)}
              placeholder="ex. Course fun kart"
              className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white/80 mb-1">Fichier MP3</label>
            {file ? (
              <div className="bg-white/10 border border-white/20 rounded-lg px-3 py-2.5 flex items-center gap-2">
                <span className="text-xl">🎵</span>
                <span className="text-white text-sm flex-1 truncate">{file.name}</span>
                <button
                  type="button"
                  onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = '' }}
                  className="text-white/40 hover:text-white text-lg leading-none transition"
                >
                  ×
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current.click()}
                className="w-full h-24 border-2 border-dashed border-white/20 rounded-xl flex flex-col items-center justify-center gap-1.5 text-white/40 hover:border-blue-400 hover:text-blue-400 transition"
              >
                <span className="text-3xl">🎵</span>
                <span className="text-sm">Cliquer pour sélectionner un fichier MP3</span>
                <span className="text-xs text-white/30">MP3 uniquement · max {MAX_SIZE_MB} Mo</span>
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="audio/mpeg,.mp3"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>

          {error && <p className="text-red-400 text-sm">{error}</p>}

          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={closeModal} className="px-4 py-2 text-white/60 hover:text-white transition">
              Annuler
            </button>
            <button type="submit" disabled={saving || !file} className="btn-primary px-6 py-2 disabled:opacity-60">
              {saving ? 'Import en cours…' : 'Importer'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal — Renommer */}
      <Modal
        open={modal !== null && modal !== 'add'}
        onClose={closeModal}
        title="Renommer le morceau"
      >
        <form onSubmit={handleRename} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white/80 mb-1">Titre du morceau</label>
            <input
              required
              value={titre}
              onChange={e => setTitre(e.target.value)}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {error && <p className="text-red-400 text-sm">{error}</p>}

          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={closeModal} className="px-4 py-2 text-white/60 hover:text-white transition">
              Annuler
            </button>
            <button type="submit" disabled={saving} className="btn-primary px-6 py-2 disabled:opacity-60">
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
